import { capitalize, compact } from 'lodash'
import { getField } from 'vuex-map-fields'

import { filterTextablePhones } from '@/utilities/filter-functions'
import { partyName } from '@/utilities'

export const getters = {
  alert: state => state.alert,
  createRecordDialogOpen: state => state.dialogOpenStates.createRecord,
  currentRecordParties: state => state.models.currentRecordParties,
  currentUserIsAdmin: state => state.currentUser?.isAdmin,
  currentUser: state => state.currentUser,
  currentUserActiveTeam: state => state.currentUser?.active_team,
  currentUserActiveTeamId: state => state.currentUser?.active_team_id,
  dialogEditRecord: state => state.dialogOpenStates.editRecord,

  docketEntryTypesData: state => state.models.docketEntryTypes,
  documentSets: state => state.models.documentSets,

  docketEntries: state => state.models.currentDocketEntries,
  docketEntrySettingDocketEntryTypes: state => state.models.filteredDocketEntryTypes || [],
  editCaseDialogOpen: state => state.dialogOpenStates.editCase,
  editEngagementLetterDialog: state => state.dialogOpenStates.editEngagementLetter,
  editRecordDialogOpen: state => state.dialogOpenStates.editRecord,
  editUserDialog: state => state.dialogOpenStates.editUser,
  entriesMassAssignmentDialogState: state => state.dialogOpenStates.openEntriesMassAssignmentDialog,
  recordSearchData: state => state.recordSearchData,
  roles: state => state.models.partyRoles,
  teams: state => state.models.teams,
  users: state => state.models.users,
  userTypeOptions: state => state.models.userTypeOptions,
  siteSettings: state => state.siteSettings,

  getField,

  /** USERS */
  refreshTokenExpiresAtWarnLimit: state => state.refreshTokenExpiresAtWarnLimit,
  refreshTokenExpiresAt: state => state.refreshTokenExpiresAt,
  isLoggedIn: state => !!state.token,
  sessionActivityWindowIn: state => state.sessionActivityWindowIn,
  isSessionInactive: state => state.sessionInactive,
  sessionReturnTo: state => state.sessionReturnTo,
  sessionWarnBeforeIn: state => state.sessionWarnBeforeIn,

  allUsers: state => state.models.users,

  getUserById: state => id => state.models.users.find(u => u.id === id),

  /**
   * We exerce a `AND` filtering.
   *
   * Accepted filters are:
   * - considerTeam: boolean (default: false)
   * - includeAll: boolean (default: true)
   * - isAssignable: boolean (default: false)
   * - onlyAttorneys: boolean (default: false)
   * - teams: Array<integer>
   * - userTypes: Array<string>
   * - includeIds: Array<integer> (default: [])
   */
  usersOptions: state => (filters = {}) => {
    const options = []

    filters = {
      considerTeam: false,
      excludeIds: [],
      includeAll: true,
      includeIds: [],
      isAssignable: false,
      onlyAttorneys: false,
      ...filters,
    }

    const userBelongsToTeams = user => {
      // NOTE: There is a discrepancy in the data. Some users belong to no
      // team and have `all_teams` set to `false`. Since these users are not
      // supposed to be able to access any legal cases in any way, we consider
      // they are not eligible to the teams filter.
      if (user.all_teams === false && user.teams.length === 0) return false

      return (
        user.all_teams ||
        (filters.teams && (
          filters.teams.includes('all_teams') || user.teams.filter(t => filters.teams.includes(t.id))
        ))
      )
    }

    const userMatchesFilters = user => {
      if (filters.onlyAttorneys && !user.is_attorney) return false
      if (filters.considerTeam && !userBelongsToTeams(user)) return false
      if (filters.isAssignable && !user.is_assignable === filters.isAssignable) return false
      if (filters.userTypes && !filters.userTypes.includes(user.user_type)) return false

      return true
    }

    state.models.users.forEach(user => {
      if (filters.excludeIds?.includes(user.id)) return
      if (user.deactivated && !filters.includeAll && !filters.includeIds?.includes(user.id)) return

      const isAdmin = user.user_type === 'admin'
      const userData = {
        all_teams: user.all_teams,
        disabled: user.deactivated,
        displayName: user.display_name,
        firstName: user.first_name,
        fullName: `${user.first_name} ${user.last_name}`,
        isAdmin: isAdmin,
        isAttorney: user.is_attorney,
        isDeactivated: user.deactivated,
        lastName: user.last_name,
        login: user.login,
        teams: user.teams,
        text: user.deactivated ? `${user.login} (inactive)` : user.login,
        userType: capitalize(user.user_type),
        value: user.id,
      }

      if (filters.includeAll || userMatchesFilters(user)) options.push(userData)
    })

    return options
  },

  allUsersOptions: state => _ => {
    return state.models.users.map(user => {
      const isAdmin = user.user_type === 'admin'
      const isDeactivated = user.deactivated

      return {
        displayName: user.display_name,
        fullName: `${user.first_name} ${user.last_name}`,
        isAdmin: isAdmin,
        isDeactivated: isDeactivated,
        text: user.login,
        value: user.id,
      }
    })
  },

  /** RECORDS */
  recordPartiesOptions: state => {
    return (
      Array.isArray(state.models.currentRecordParties) &&
      state.models.currentRecordParties.map(item => {
        return {
          text: partyName(item),
          value: item.id,
        }
      })
    )
  },
  recordPartiesMobileTextingPhonesOptions: state => {
    const mtParties = Array.isArray(state.models.currentRecordParties) &&
      state.models.currentRecordParties.filter(party => {
        return party.phones.find(filterTextablePhones) // get parties with mobile texting
      })
    return (
      mtParties.map(item => {
        return {
          text: partyName(item),
          value: item.id,
          phones: item.phones.filter(filterTextablePhones), // get only the mobile texting phone numbers
        }
      })
    )
  },
  recordHolderParty: state => {
    if (state.models.currentRecord === null || state.models.currentRecord === undefined ||
      state.models.currentRecordParties.length === 0) return {}

    // NAMESPACED STORE TODO
    const recordHolder = state.models.currentRecordParties.find(p => {
      return p.id === state.models.currentRecord.record_holder_party_id
    })

    return recordHolder
  },

  /** CASES */
  legalCaseTypesIds: state => {
    const items = {}

    state.models.legalCaseTypes.forEach(item => {
      items[item.id] = {
        fullName: `${item.name} - ${item.description}`,
        shortName: item.name,
        description: item.description,
      }
    })

    return items
  },

  legalCaseTypesOptions: state => {
    return state.models.legalCaseTypes.map(item => {
      return {
        checklist_id: item.checklist_id,
        description: item.description,
        has_rules: item.has_workflows,
        name: item.name,
        text: `${item.name} - ${item.description}`,
        value: item.id,
      }
    })
  },

  legalCaseTypes: state => state.models.legalCaseTypes,

  /** DOCKET ENTRIES */
  // TODO: add filtering based on legal case type and teams
  // once implemented on back-end
  docketEntryTypes: state => entryType => {
    const options = []

    state.models.docketEntryTypes.forEach(item => {
      if (item.action_type === entryType || !entryType) {
        options.push({
          text: item.name,
          value: item.id,
          type: item.action_type,
          has_workflows: item.has_workflows,
        })
      }
    })

    return options
  },

  filteredDocketEntryTypes: state => () => {
    return state.models.filteredDocketEntryTypes.map(item => {
      return {
        text: item.name,
        value: item.id,
        type: item.action_type,
        has_workflows: item.has_workflows,
      }
    })
  },

  /** SETTINGS data */
  // Countries
  countriesIds: state => {
    const items = {}

    state.models.countries.forEach(item => {
      items[item.id] = capitalize(item.name)
    })

    return items
  },

  countriesOptions: state => {
    return state.models.countries.map(item => {
      return {
        text: capitalize(item.name),
        value: item.id,
      }
    })
  },

  // Languages
  languagesIds: state => {
    const items = {}

    state.models.languages.forEach(item => {
      items[item.id] = capitalize(item.name)
    })

    return items
  },

  languagesOptions: state => {
    return state.models.languages.map(item => {
      return {
        text: item.name,
        value: item.id,
      }
    })
  },

  // Locations
  locationsIds: state => {
    const items = {}

    state.models.locations.forEach(item => {
      items[item.id] = capitalize(item.description)
    })

    return items
  },

  locationsOptions: state => {
    return state.models.locations.map(item => {
      return {
        // Might want name concatenated with description, with description truncated if long
        text: item.name,
        value: item.id,
      }
    })
  },

  // PreferenceCategories
  preferenceCategoriesIds: state => {
    const items = {}

    state.models.preferenceCategories.forEach(item => {
      items[item.id] = `${item.name} - ${item.description}`
    })

    return items
  },

  preferenceCategoriesOptions: state => {
    return state.models.preferenceCategories.map(item => {
      return {
        text: `${item.name} - ${item.description}`,
        value: item.id,
      }
    })
  },

  genderOptions: state => {
    return state.models.genders.map(item => {
      return {
        text: item.name,
        value: item.id,
      }
    })
  },

  genders: state => state.models.genders,

  teamsOptions: state => {
    return state.models.teams.map(item => {
      return {
        text: item.name,
        value: item.id,
      }
    })
  },

  currentEditedParty: state => state.models.editedParty,

  identifiersOptions: _ => [
    {
      text: 'A#',
      value: 'additional_a_number',
    },
    {
      text: 'SSN',
      value: 'additional_ssn',
    },
    {
      text: 'Other ID',
      value: 'additional_other_id',
    },
  ],

  availableANumbersOptions: state => {
    const editedParty = state.models.editedParty
    if (!editedParty) return []

    const additionalNumbers = compact(editedParty.identifiers?.map(identifier => {
      if (identifier.kind === 'additional_a_number') return identifier.value
      return null
    }))

    return [
      editedParty.a_number,
      ...additionalNumbers,
    ]
  },

  getCurrentUser: state => state.currentUser,
}
